import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import MaterialTable from 'material-table';
import moment from 'moment/min/moment-with-locales';
import { api } from 'common';
import { ThemeProvider } from '@mui/material/styles';
import { Alert, Autocomplete, Box, Chip, Grid, TextField, Typography, createFilterOptions } from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import localization from '../components/Table/Localization';
import C2Dialog, { C2PrimaryButton, C2SecondaryButton } from '../components/C2Dialog';
import {
  C2,
  C2_FONT,
  c2TableTheme,
  c2TableOptions,
  c2TableStyle,
  c2Localization,
  C2TableCard,
  C2SectionHeading,
  C2EmptyState,
  C2SummaryCard,
  C2StatusChip,
} from '../components/Table/C2UserTable';

const QUICK_AMOUNTS = [10000, 20000, 50000, 100000, 200000];

const ROLE_LABELS = {
  driver: 'Conductor',
  customer: 'Usuario',
  fleetadmin: 'Flota',
};

const STATUS = {
  done: { tone: 'success', label: 'Acreditada' },
  processing: { tone: 'warning', label: 'En proceso' },
  failed: { tone: 'danger', label: 'Fallida' },
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontFamily: C2_FONT,
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: C2.border },
    '&:hover fieldset': { borderColor: C2.bright },
    '&.Mui-focused fieldset': { borderColor: C2.violet },
  },
  '& .MuiInputLabel-root': { fontFamily: C2_FONT, color: C2.muted },
  '& .MuiInputLabel-root.Mui-focused': { color: C2.violet },
  '& .MuiFormHelperText-root': { fontFamily: C2_FONT },
};

const fullName = (u) => `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || '—';
const formatCop = (v) => api.formatCop(Number(v) || 0);

const filterUsers = createFilterOptions({
  stringify: (u) => `${fullName(u)} ${u.email || ''} ${u.mobile || ''}`,
  limit: 50,
});

function SummaryRow({ label, value, strong, accent }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, py: 1, borderTop: '1px solid rgba(235,231,251,0.14)' }}>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.85rem', color: 'rgba(251,250,254,0.72)' }}>{label}</Typography>
      <Typography
        sx={{
          fontFamily: C2_FONT,
          fontWeight: strong ? 800 : 700,
          fontSize: strong ? '1.35rem' : '0.95rem',
          color: accent || C2.white,
          textAlign: 'right',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function LockedState() {
  return (
    <C2TableCard>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: { xs: 5, sm: 7 }, px: 2, gap: 1.5 }}>
        <Box sx={{ width: 88, height: 88, borderRadius: '50%', backgroundColor: C2.lavender, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LockOutlinedIcon sx={{ fontSize: 42, color: C2.violet }} />
        </Box>
        <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '1.1rem', color: C2.sidebar }}>
          Recargas restringidas
        </Typography>
        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.92rem', color: C2.muted, maxWidth: 440, lineHeight: 1.5 }}>
          Solo el administrador principal de Conecta2 puede autorizar recargas manuales de billetera. Si un usuario necesita saldo, solicítalo al administrador principal.
        </Typography>
      </Box>
    </C2TableCard>
  );
}

export default function AddMoney() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const profile = useSelector((state) => state.auth.profile);
  const settings = useSelector((state) => state.settingsdata.settings);
  const allUsers = useSelector((state) => state.usersdata.users);
  const authorized = api.canAuthorizeWalletCredit(profile);
  const showContact = !!settings?.AllowCriticalEditsAdmin;

  const [selectedUid, setSelectedUid] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [requestId, setRequestId] = useState(() => api.newWalletCreditRequestId());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);
  const [credits, setCredits] = useState([]);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const loadCredits = useCallback(async () => {
    setLoadingCredits(true);
    try {
      setCredits(await api.fetchWalletAdminCredits());
    } catch (e) {
      setCredits([]);
    } finally {
      setLoadingCredits(false);
    }
  }, []);

  useEffect(() => {
    if (authorized) loadCredits();
  }, [authorized, loadCredits]);

  const userOptions = useMemo(
    () =>
      (allUsers || [])
        .filter((u) => u.usertype !== 'admin')
        .sort((a, b) => fullName(a).localeCompare(fullName(b), 'es')),
    [allUsers]
  );

  const selectedUser = useMemo(
    () => (selectedUid ? userOptions.find((u) => u.id === selectedUid) || null : null),
    [userOptions, selectedUid]
  );

  const numericAmount = Math.round(Number(amount) || 0);
  const currentBalance = Number(selectedUser?.walletBalance) || 0;
  const trimmedNote = note.trim();
  const canSubmit = !!selectedUser && numericAmount > 0 && trimmedNote.length >= 3 && !submitting;

  useEffect(() => {
    setRequestId(api.newWalletCreditRequestId());
  }, [selectedUid, numericAmount]);

  const summary = useMemo(() => {
    const done = credits.filter((c) => c.status === 'done');
    return {
      count: done.length,
      total: done.reduce((acc, c) => acc + (Number(c.amount) || 0), 0),
      last: done[0]?.createdAt || null,
    };
  }, [credits]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const res = await api.adminAddToWallet({
      uid: selectedUser.id,
      amount: numericAmount,
      note: trimmedNote,
      requestId,
    });
    setSubmitting(false);
    setConfirmOpen(false);
    if (res?.success) {
      setNotice({
        severity: 'success',
        msg: `Se acreditaron ${formatCop(res.amount)} a ${fullName(selectedUser)}. Nuevo saldo: ${formatCop(res.balanceAfter)}.`,
      });
      setSelectedUid(null);
      setAmount('');
      setNote('');
      setRequestId(api.newWalletCreditRequestId());
      loadCredits();
    } else {
      setNotice({ severity: 'error', msg: res?.error || 'No se pudo completar la recarga.' });
      if (!res?.network) setRequestId(api.newWalletCreditRequestId());
    }
  };

  const hideContact = (value) => (showContact ? value || '—' : '••••••');

  const columns = [
    { title: 'Fecha', field: 'createdAt', defaultSort: 'desc', render: (r) => (r.createdAt ? moment(r.createdAt).format('lll') : '—') },
    {
      title: 'Usuario',
      field: 'userName',
      render: (r) => (
        <Box sx={{ textAlign: 'left' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>{r.userName || '—'}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted }}>
            {[ROLE_LABELS[r.usertype] || r.usertype, showContact ? r.userEmail : null].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Monto',
      field: 'amount',
      render: (r) => (
        <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '0.92rem', color: '#15803D', whiteSpace: 'nowrap' }}>
          + {formatCop(r.amount)}
        </Typography>
      ),
    },
    {
      title: 'Saldo',
      field: 'balanceAfter',
      render: (r) =>
        r.status === 'done' ? (
          <Box sx={{ whiteSpace: 'nowrap' }}>
            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted }}>Antes {formatCop(r.balanceBefore)}</Typography>
            <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.86rem', color: C2.sidebar }}>Después {formatCop(r.balanceAfter)}</Typography>
          </Box>
        ) : (
          '—'
        ),
    },
    { title: 'Motivo', field: 'note', render: (r) => r.note || '—' },
    { title: 'Autorizó', field: 'adminEmail', render: (r) => hideContact(r.adminEmail) },
    {
      title: 'Estado',
      field: 'status',
      render: (r) => {
        const s = STATUS[r.status] || STATUS.processing;
        return <C2StatusChip tone={s.tone} label={s.label} />;
      },
    },
  ];

  return (
    <Box sx={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <ThemeProvider theme={c2TableTheme}>
        {!authorized ? (
          <LockedState />
        ) : (
          <>
            {notice ? (
              <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}>
                {notice.msg}
              </Alert>
            ) : null}

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <C2SummaryCard icon={ReceiptLongOutlinedIcon} label="Recargas realizadas" value={summary.count} caption="Últimos 100 registros" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <C2SummaryCard icon={SavingsOutlinedIcon} label="Total acreditado" value={formatCop(summary.total)} caption="Suma de recargas acreditadas" accent="#16A34A" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <C2SummaryCard
                  icon={EventAvailableOutlinedIcon}
                  label="Última recarga"
                  value={summary.last ? moment(summary.last).format('ll') : '—'}
                  caption={summary.last ? moment(summary.last).format('LT') : 'Aún no hay recargas'}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={7}>
                <C2TableCard sx={{ height: '100%' }}>
                  <C2SectionHeading title="Nueva recarga" subtitle="Selecciona el usuario, el monto y el motivo. El saldo se acredita al confirmar." />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Autocomplete
                        options={userOptions}
                        value={selectedUser}
                        onChange={(_, v) => setSelectedUid(v ? v.id : null)}
                        filterOptions={filterUsers}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        getOptionLabel={(o) => fullName(o)}
                        renderOption={(props, o) => (
                          <li {...props} key={o.id}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>{fullName(o)}</Typography>
                                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted }}>
                                  {[showContact ? o.email : null, showContact ? o.mobile : null, o.cityName].filter(Boolean).join(' · ') || '—'}
                        </Typography>
                              </Box>
                              <C2StatusChip tone="violet" dot={false} label={ROLE_LABELS[o.usertype] || o.usertype || '—'} />
                            </Box>
                          </li>
                        )}
                        noOptionsText="Sin usuarios"
                        renderInput={(params) => (
                          <TextField {...params} label="Usuario a recargar" placeholder="Busca por nombre, correo o celular" sx={fieldSx} />
                        )}
                      />
                    </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                        label="Monto a acreditar (COP)"
                                    value={amount}
                        onChange={(e) => setAmount(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        inputProps={{ inputMode: 'numeric' }}
                        helperText={numericAmount > 0 ? formatCop(numericAmount) : 'Solo números, sin puntos ni comas.'}
                        sx={fieldSx}
                      />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
                        {QUICK_AMOUNTS.map((v) => {
                          const active = numericAmount === v;
                          return (
                            <Chip
                              key={v}
                              label={formatCop(v)}
                              onClick={() => setAmount(String(v))}
                              sx={{
                                fontFamily: C2_FONT,
                                fontWeight: 700,
                                borderRadius: '10px',
                                border: `1px solid ${active ? C2.violet : C2.border}`,
                                color: active ? C2.white : C2.violet,
                                background: active ? `linear-gradient(90deg, ${C2.violet} 0%, ${C2.bright} 100%)` : C2.lavender,
                                '&:hover': { background: active ? `linear-gradient(90deg, ${C2.violet} 0%, ${C2.bright} 100%)` : C2.border },
                              }}
                            />
                          );
                        })}
                      </Box>
                            </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Motivo de la recarga"
                        placeholder="Ej.: pago en efectivo en oficina, compensación por viaje, ajuste…"
                        value={note}
                        onChange={(e) => setNote(e.target.value.slice(0, 300))}
                        helperText={`${note.length}/300 · Obligatorio, queda en el historial.`}
                        sx={fieldSx}
                      />
                            </Grid>
                        </Grid>
                </C2TableCard>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '16px',
                    p: { xs: 2, sm: 2.5 },
                    color: C2.white,
                    background: `linear-gradient(145deg, ${C2.deep} 0%, ${C2.sidebar} 100%)`,
                    boxShadow: '0 12px 28px rgba(26,15,59,0.22)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ position: 'absolute', right: -40, top: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(118,40,255,0.45) 0%, rgba(118,40,255,0) 70%)' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, position: 'relative' }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${C2.violet}, ${C2.bright})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <AccountBalanceWalletOutlinedIcon sx={{ color: C2.white, fontSize: 22 }} />
                    </Box>
                    <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.95rem' }}>
                      Resumen de la recarga
                    </Typography>
                  </Box>

                  <Box sx={{ position: 'relative', flex: 1 }}>
                    <SummaryRow label="Usuario" value={selectedUser ? fullName(selectedUser) : '—'} />
                    <SummaryRow label="Tipo" value={selectedUser ? ROLE_LABELS[selectedUser.usertype] || selectedUser.usertype || '—' : '—'} />
                    <SummaryRow label="Saldo actual" value={selectedUser ? formatCop(currentBalance) : '—'} />
                    <SummaryRow label="Recarga" value={numericAmount > 0 ? `+ ${formatCop(numericAmount)}` : '—'} accent="#86EFAC" />
                    <SummaryRow label="Nuevo saldo" value={selectedUser && numericAmount > 0 ? formatCop(currentBalance + numericAmount) : '—'} strong />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1.5, p: 1.25, borderRadius: '12px', backgroundColor: 'rgba(235,231,251,0.08)', position: 'relative' }}>
                    <VerifiedUserOutlinedIcon sx={{ fontSize: 18, color: '#C4B5FD', mt: '2px' }} />
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', color: 'rgba(251,250,254,0.8)', lineHeight: 1.45 }}>
                      Queda registrada a nombre de {profile?.email}. El usuario recibe una notificación y la recarga no genera bonos de red.
                    </Typography>
                  </Box>

                  <C2PrimaryButton
                    fullWidth
                    startIcon={<AddCardOutlinedIcon />}
                    disabled={!canSubmit}
                    onClick={() => setConfirmOpen(true)}
                    sx={{ mt: 2, py: 1.25, position: 'relative', '&.Mui-disabled': { background: 'rgba(235,231,251,0.16)', color: 'rgba(251,250,254,0.5)', boxShadow: 'none' } }}
                  >
                    Acreditar saldo
                  </C2PrimaryButton>
                </Box>
                </Grid>
            </Grid>

            <C2TableCard>
              <C2SectionHeading title="Historial de recargas" subtitle="Recargas manuales autorizadas desde este panel." />
              <MaterialTable
                title="Historial de recargas"
                columns={columns}
                data={credits}
                isLoading={loadingCredits}
                style={c2TableStyle(isRTL)}
                onRowClick={(evt, row) => setSelectedRow(row.tableData.id)}
                options={{ ...c2TableOptions(selectedRow), pageSize: 10, pageSizeOptions: [10, 20, 50] }}
                localization={c2Localization(
                  localization(t),
                  <C2EmptyState
                    icon={AccountBalanceWalletOutlinedIcon}
                    title="Aún no hay recargas"
                    message="Cuando acredites saldo a un conductor o usuario, la recarga quedará registrada aquí."
                  />
                )}
              />
            </C2TableCard>

            <C2Dialog
              open={confirmOpen}
              onClose={() => (submitting ? null : setConfirmOpen(false))}
              title="Confirmar recarga"
              icon={AddCardOutlinedIcon}
              maxWidth={460}
              actions={
                <>
                  <C2SecondaryButton onClick={() => setConfirmOpen(false)} disabled={submitting}>
                    Cancelar
                  </C2SecondaryButton>
                  <C2PrimaryButton onClick={handleSubmit} disabled={submitting}>
                    {submitting ? 'Acreditando…' : 'Confirmar recarga'}
                  </C2PrimaryButton>
                </>
              }
            >
              {selectedUser ? (
                <Box sx={{ fontFamily: C2_FONT, color: C2.dark, fontSize: '0.9rem', lineHeight: 1.55 }}>
                  <Typography sx={{ fontFamily: C2_FONT, mb: 1.25 }}>
                    Vas a acreditar <b>{formatCop(numericAmount)}</b> a la billetera de <b>{fullName(selectedUser)}</b> ({ROLE_LABELS[selectedUser.usertype] || selectedUser.usertype}).
                  </Typography>
                  <Box sx={{ p: 1.5, borderRadius: '12px', backgroundColor: C2.bg, border: `1px solid ${C2.border}` }}>
                    {[
                      ['Saldo actual', formatCop(currentBalance)],
                      ['Nuevo saldo', formatCop(currentBalance + numericAmount)],
                      ['Motivo', trimmedNote],
                    ].map(([label, value]) => (
                      <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
                        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.82rem', color: C2.muted }}>{label}</Typography>
                        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', fontWeight: 700, color: C2.sidebar, textAlign: 'right', wordBreak: 'break-word' }}>{value}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography sx={{ fontFamily: C2_FONT, mt: 1.25, fontWeight: 700, color: '#B91C1C', fontSize: '0.84rem' }}>
                    El saldo se acredita de inmediato y no se revierte automáticamente.
                  </Typography>
                </Box>
              ) : null}
            </C2Dialog>
          </>
        )}
      </ThemeProvider>
    </Box>
  );
}
